import { useState, useEffect, useRef, useCallback } from 'react';

interface UseScrollDirectionOptions {
  threshold?: number;
  initialVisible?: boolean;
}

/**
 * Hook to detect scroll direction and control header visibility
 * Returns true when header should be visible (scrolling up or at top)
 * Returns false when header should be hidden (scrolling down)
 */
export function useScrollDirection(options: UseScrollDirectionOptions = {}) {
  const { threshold = 10, initialVisible = true } = options;
  const [isVisible, setIsVisible] = useState(initialVisible);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const updateVisibility = useCallback(() => {
    const currentScrollY = window.scrollY;
    const diff = currentScrollY - lastScrollY.current;

    if (Math.abs(diff) > threshold) {
      // Show header when scrolling up OR when near the top of the page
      const shouldShow = diff < 0 || currentScrollY < threshold;
      setIsVisible(shouldShow);
      lastScrollY.current = currentScrollY;
    }

    ticking.current = false;
  }, [threshold]);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        // Use requestAnimationFrame for better performance
        window.requestAnimationFrame(updateVisibility);
        ticking.current = true;
      }
    };

    // Initialize lastScrollY
    lastScrollY.current = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [updateVisibility]);

  return isVisible;
}

/**
 * Simpler version that just returns the visibility state
 */
export function useHeaderVisible(threshold = 15): boolean {
  return useScrollDirection({ threshold });
}
